'use client'

import * as React from 'react'
import { Button, Input, Spinner, Textarea, Tooltip } from '@nextui-org/react'

import { cn } from './cn'
import { useCallback, useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import api from 'src/lib/api'

interface CompanyDetailsProps {
  className?: string
  workspace: any
  setWorkspace: (w: any) => void
}
import { useDropzone } from 'react-dropzone'
import { RefreshCcwIcon, TrashIcon } from 'lucide-react'
import toast from 'react-hot-toast'
import { useSocketContextData } from 'src/context/socket'

const CompanyDetails = React.forwardRef<HTMLDivElement, CompanyDetailsProps>(
  ({ className, workspace, setWorkspace, ...props }, ref) => {
    const [loading, setLoading] = useState(false)
    const [biography, setBiography] = useState<string>('')
    const [productOffering, setProductOffering] = useState<string>('')
    const [contactPerson, setContactPerson] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [phone, setPhone] = useState<string>('')
    const [url, setUrl] = useState<string>('')
    const [address, setAddress] = useState<string>('')
    const [taxId, setTaxID] = useState<string>('')
    const [logo, setLogo] = useState<string>('')
    const [file, setFile] = useState<File>()
    const [companyName, setCompanyName] = useState<string>('')

    const { io } = useSocketContextData()
    const [scraping, setScraping] = useState<boolean>(false)
    const [beautifying, setBeautifying] = useState<boolean>(false)
    const [productBeautifying, setProductBeautifying] = useState<boolean>(false)

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
      setFile(acceptedFiles[0])
      if (acceptedFiles[0]) {
        const preview = URL.createObjectURL(acceptedFiles[0])
        setLogo(preview)
      }
    }, [])
    const { getRootProps, getInputProps, open } = useDropzone({
      onDrop,
      accept: {
        'image/*': ['.jpg', '.jpeg', '.png'],
      },
      maxFiles: 1,
    })
    useEffect(() => {
      if (workspace) {
        setBiography(workspace.biography ? workspace.biography : '')
        setContactPerson(workspace.contactPerson ? workspace.contactPerson : '')
        setEmail(workspace.email ? workspace.email : '')
        setPhone(workspace.phone ? workspace.phone : '')
        setUrl(workspace.url ? workspace.url : '')
        setCompanyName(workspace.companyName ? workspace.companyName : '')
        setAddress(workspace.address ? workspace.address : '')
        setTaxID(workspace.taxId ? workspace.taxId : '')
        setLogo(workspace.logo ? workspace.logo : '')
        setProductOffering(workspace.productOffering ? workspace.productOffering : '')
      }
    }, [workspace])

    const updateCompany = async () => {
      setLoading(true)
      const formData = new FormData()
      formData.append('biography', biography)
      formData.append('productOffering', productOffering)
      formData.append('contactPerson', contactPerson)
      formData.append('companyName', companyName)
      formData.append('email', email)
      formData.append('phone', phone)
      formData.append('url', url)
      formData.append('address', address)
      formData.append('taxId', taxId)
      formData.append('fineTuneText', workspace.fineTuneText)
      formData.append('defaultDocLength', workspace.defaultDocLength)
      formData.append('audianceType', workspace.audianceType)
      formData.append('modeOfAddress', workspace.modeOfAddress)
      formData.append('formality', workspace.formality)
      if (file) formData.append('file', file)
      // this only to delete the logo in case the user decided to remove it, we only set empty logo when the previous value of the logo was set to AWS link
      if (workspace.logo !== '') formData.append('logo', logo)
      const res = await api.post(`/workspaces/${workspace.uuid}`, formData)
      if (res.data?.uuid) {
        setWorkspace(res.data)
      }
      setLoading(false)
      toast.success('Company profile updated!', {
        position: 'top-center',
        style: {
          borderRadius: '10px',
          background: '#333',
          color: '#fff',
        },
      })
    }
    const deleteFile = async () => {
      setLogo('')
      setFile(undefined)
    }
    const triggerScrape = () => {
      if (!url || !io) return
      setScraping(true)
      io.emit('getCompanyProfile', { url: url }, (response) => {
        if (response?.description) {
          setBiography(response.description)
        }
        if (response?.productOffering) {
          setProductOffering(response.productOffering)
        }
        if (response?.name) {
          setCompanyName(response.name)
        }
        setScraping(false)
      })
    }
    return (
      <>
        <div className="flex w-full pt-2 pb-6 px-2 pl-0">
          <header className="flex w-full justify-between">
            <div className="flex flex-col">
              <h1 className="text-xl font-bold text-default-900 lg:text-3xl">Company details</h1>
              <p className="text-small text-default-700 lg:text-medium">
                Your company details and contact information.
              </p>
            </div>
          </header>
        </div>
        <div ref={ref} className={cn('flex flex-col p-0 w-full gap-4', className)} {...props}>
          <div className="flex gap-2 items-end border rounded-xl p-4">
            <Input
              label={
                <span className={'flex flex-row items-center gap-1'}>
                  <span className="font-medium">Website</span> of your Company{' '}
                  <Tooltip content="Enter your company URL" color="foreground">
                    <Icon icon={'lucide:circle-help'} />
                  </Tooltip>
                </span>
              }
              placeholder="e.g. acme.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              labelPlacement="outside"
              type="text"
              className="flex-1"
              isDisabled={scraping}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  triggerScrape()
                }
              }}
            />
            <Button
              color="secondary"
              variant="solid"
              isDisabled={scraping}
              isLoading={scraping}
              onPress={triggerScrape}
            >
              Scrape data from website
            </Button>
          </div>

          <div className="flex flex-col gap-5 border rounded-xl p-4">
            <Textarea
              label={
                <span className={'flex flex-row items-center gap-1'}>
                  <span className="font-medium">Info</span> about your Company{' '}
                  <Tooltip content="Provide a brief description about your company" color="foreground">
                    <Icon icon={'lucide:circle-help'} />
                  </Tooltip>
                </span>
              }
              placeholder="e.g., Our platform enables finance teams in mid-market companies to automate invoice processing, vendor onboarding, and compliance workflows. We integrate seamlessly with leading ERP systems to reduce manual effort and improve audit readiness."
              labelPlacement={'outside'}
              value={biography ? biography : ''}
              onChange={(e) => setBiography(e.target.value)}
              isDisabled={scraping || beautifying}
              endContent={
                <Button
                  color="secondary"
                  variant="light"
                  size="sm"
                  onClick={async () => {
                    if (!io || !biography) return
                    setBeautifying(true)
                    io.emit(
                      'textProcess',
                      {
                        selectedText: biography,
                        content: biography,
                        token: localStorage.getItem('accessToken'),
                        operation: 'grammarCheck',
                      },
                      (res) => {
                        if (res?.text) {
                          setBiography(res.text)
                        }
                        setBeautifying(false)
                      },
                    )
                  }}
                  isDisabled={beautifying}
                  isLoading={beautifying}
                >
                  Beautify
                </Button>
              }
            />
            <Textarea
              label={
                <span className={'flex flex-row items-center gap-1'}>
                  <span className="font-medium">Product Offering</span> of your Company{' '}
                  <Tooltip
                    content="Briefly describe the main products or services your company offers"
                    color="foreground"
                  >
                    <Icon icon={'lucide:circle-help'} />
                  </Tooltip>
                </span>
              }
              placeholder="e.g., Our product suite streamlines financial operations by automating expense reporting, budget approvals, and audit preparation. With deep integrations into ERP and accounting platforms, we help finance teams operate with greater speed, accuracy, and control."
              labelPlacement={'outside'}
              value={productOffering ? productOffering : ''}
              onChange={(e) => setProductOffering(e.target.value)}
              isDisabled={scraping || productBeautifying}
              endContent={
                <Button
                  color="secondary"
                  variant="light"
                  size="sm"
                  onClick={async () => {
                    if (!io || !productOffering) return
                    setProductBeautifying(true)
                    io.emit(
                      'textProcess',
                      {
                        selectedText: productOffering,
                        content: productOffering,
                        token: localStorage.getItem('accessToken'),
                        operation: 'grammarCheck',
                      },
                      (res) => {
                        if (res?.text) {
                          setProductOffering(res.text)
                        }
                        setProductBeautifying(false)
                      },
                    )
                  }}
                  isDisabled={productBeautifying}
                  isLoading={productBeautifying}
                >
                  Beautify
                </Button>
              }
            />
          </div>

          <div className="flex flex-col gap-5 border rounded-xl p-4">
            <div>
              <span className={'flex flex-row items-center gap-1 text-small'}>
                <span className="font-medium">Logo</span> of your Company{' '}
                <Tooltip content="Upload your company logo" color="foreground">
                  <Icon icon={'lucide:circle-help'} />
                </Tooltip>
              </span>
              <div
                {...getRootProps({
                  className: 'flex flex-col gap-2 mt-2 bg-default-100 rounded-xl justify-center items-center',
                })}
              >
                <input {...getInputProps()} />
                <div className={'flex flex-col justify-center items-center p-4'}>
                  {logo ? (
                    <>
                      <img src={logo} className={'w-[120px] mb-5'} />
                      <div className={'flex gap-2'}>
                        <Button variant="solid" color={'secondary'} className={''} onPress={open}>
                          <RefreshCcwIcon />
                          Select file
                        </Button>
                        <Button variant="bordered" color={'danger'} className={''} onPress={deleteFile}>
                          <TrashIcon />
                          Delete File
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={'bg-white p-2 rounded-full mb-2'}>
                        <Icon icon={'lucide:cloud-upload'} />
                      </div>
                      <p className={'font-regular text-sm'}>
                        <span className={'pr-1 font-medium text-blue-400 cursor-pointer'} onClick={open}>
                          Click to upload
                        </span>
                        or drag and drop
                      </p>
                      <span className={'text-xs font-regular'}>SVG, PNG, JPG or GIF (max.800x400px)</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
              <Input
                className="mt-2"
                label={
                  <span className={'flex flex-row items-center gap-1'}>
                    <span className="font-medium">Name</span> of your Company{' '}
                    <Tooltip content="Enter your company name" color="foreground">
                      <Icon icon={'lucide:circle-help'} />
                    </Tooltip>
                  </span>
                }
                labelPlacement={'outside'}
                type="text"
                placeholder="e.g. ACME Inc."
                value={companyName ? companyName : ''}
                onChange={(e) => setCompanyName(e.target.value)}
              />
              <Input
                className="mt-2"
                label={
                  <span className={'flex flex-row items-center gap-1'}>
                    <span className="font-medium">Address</span> of your Company{' '}
                    <Tooltip content="Enter the company address" color="foreground">
                      <Icon icon={'lucide:circle-help'} />
                    </Tooltip>
                  </span>
                }
                type="text"
                labelPlacement={'outside'}
                placeholder="e.g. 4015 Smith St"
                value={address ? address : ''}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
              <Input
                className="mt-2"
                label={
                  <span className={'flex flex-row items-center gap-1'}>
                    Contact <span className="font-medium">Person</span>{' '}
                    <Tooltip content="Enter the name of the contact person" color="foreground">
                      <Icon icon={'lucide:circle-help'} />
                    </Tooltip>
                  </span>
                }
                labelPlacement={'outside'}
                type="text"
                placeholder="e.g. John Doe"
                value={contactPerson ? contactPerson : ''}
                onChange={(e) => setContactPerson(e.target.value)}
              />
              <Input
                className="mt-2"
                label={
                  <span className={'flex flex-row items-center gap-1'}>
                    Contact <span className="font-medium">Email</span>{' '}
                    <Tooltip content="Enter the email address of the contact person" color="foreground">
                      <Icon icon={'lucide:circle-help'} />
                    </Tooltip>
                  </span>
                }
                type="text"
                labelPlacement={'outside'}
                placeholder="e.g. john@acme.inc"
                value={email ? email : ''}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
              <Input
                className="mt-2"
                label={
                  <span className={'flex flex-row items-center gap-1'}>
                    Contact <span className="font-medium">Phone</span>{' '}
                    <Tooltip content="Enter the phone number for the contact person" color="foreground">
                      <Icon icon={'lucide:circle-help'} />
                    </Tooltip>
                  </span>
                }
                labelPlacement={'outside'}
                type="text"
                placeholder="e.g. +1 (231) 4567-8903"
                value={phone ? phone : ''}
                onChange={(e) => setPhone(e.target.value)}
              />
              <Input
                className="mt-2"
                label={
                  <span className={'flex flex-row items-center gap-1'}>
                    Tax ID{' '}
                    <Tooltip content="Enter the company tax identification number" color="foreground">
                      <Icon icon={'lucide:circle-help'} />
                    </Tooltip>
                  </span>
                }
                type="text"
                labelPlacement={'outside'}
                placeholder="e.g. xx-xxxxxxx"
                value={taxId ? taxId : ''}
                onChange={(e) => setTaxID(e.target.value)}
              />
            </div>
          </div>

          <Button
            className="w-fit"
            color="secondary"
            size="md"
            radius="md"
            onPress={updateCompany}
            isDisabled={loading}
          >
            {loading ? (
              <span className={'flex flex-row justify-center items-center gap-2'}>
                Updating information <Spinner size={'sm'} color={'white'} />
              </span>
            ) : (
              'Update Details'
            )}
          </Button>
        </div>
      </>
    )
  },
)

CompanyDetails.displayName = 'CompanyDetails'

export default CompanyDetails
